'use client'

import { useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import { createContext, useCallback, useContext } from 'react'
import { IGoogleAddressOption } from '@/features/google-places/google-places-restaurants-search'
import { Locale } from '@/setup/locales'
import { getPathname } from '@/setup/routing'
import { IChildrenProps } from '@/typings/children-props'
import { noOp } from '@/utils/functions/noOp'

interface IB2bContext {
    onSelect: (option: IGoogleAddressOption | null) => void
}

const B2bContext = createContext<IB2bContext>({
    onSelect: noOp,
})

export const B2bContextProvider = ({ children }: IChildrenProps) => {
    const locale = useLocale()

    const router = useRouter()

    const onSelect = useCallback(
        async (option: IGoogleAddressOption | null) => {
            const placeId = option?.value.placeId

            if (!placeId) return

            const placeIdSlug = slugify(option?.value.name ?? '') + '--' + placeId

            router.push(
                getPathname('/for-companies/[placeIdSlug]', { placeIdSlug }, locale as Locale)
            )
        },
        [locale, router]
    )

    return <B2bContext.Provider value={{ onSelect }}>{children}</B2bContext.Provider>
}

export const useB2bContext = () => useContext(B2bContext)

function slugify(input: string): string {
    // Normalize the string to NFD Unicode normal form, decomposing special characters
    const normalized = input.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

    // Replace non-alphanumeric characters with hyphens
    const slug = normalized
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^a-zA-Z0-9\-]/g, '') // Remove all non-word chars
        .replace(/\-\-+/g, '-') // Replace multiple hyphens with a single hyphen
        .toLowerCase() // Convert to lowercase

    return slug
}
